import { useEffect, useState } from 'react';
import { ArrowLeft, Check, FileText, Plus, Receipt } from 'lucide-react';

/**
 * Animated replica of the real WapPro procurement flow (Dhivehi/RTL), faithful
 * to the app: build an item request → send to vendors for quotes → quotes come
 * in → compare side by side (lowest per row highlighted) → award the contract
 * (quotation + invoice PDFs). Steps on a timer and loops. Strings are copied
 * from the real WapPro source.
 */

const HEADING = 'MV FK, Faruma, "Noto Sans Thaana", sans-serif';
const MONO = '"IBM Plex Mono", ui-monospace, monospace';

const PALETTE = {
  '--background': '36 24% 96.5%',
  '--paper': '38 30% 99%',
  '--foreground': '24 16% 10%',
  '--card': '38 30% 99%',
  '--primary': '178 84% 28%',
  '--muted': '33 18% 92%',
  '--muted-foreground': '24 8% 38%',
  '--border': '30 12% 84%',
  '--border-strong': '30 10% 70%',
  '--destructive': '4 65% 45%',
  '--accent-cream': '38 60% 86%',
  '--accent-ink': '24 28% 14%',
  '--success': '156 50% 32%',
  fontFamily: 'Faruma, "Noto Sans Thaana", sans-serif',
} as React.CSSProperties;

const ITEMS = [
  { name: 'އޮފީސް ގޮނޑި', qty: 12, unit: 'އަދަދު' },
  { name: 'މޭޒު', qty: 6, unit: 'އަދަދު' },
  { name: 'ފައިލް ކަބަޑު', qty: 4, unit: 'އަދަދު' },
];

const VENDORS = [
  { name: 'ސީ ފްރަންޓް', lines: [11400, 9600, 6800], awarded: false },
  { name: 'މޯލްޑިވް ފަރުނީޗަރ', lines: [9600, 8400, 6200], awarded: true },
  { name: 'ރީފް ސަޕްލައި', lines: [10800, 9000, 7200], awarded: false },
];

// 0-1 build request · 2-5 quotes arrive one vendor at a time · 6 award.
const STEP_MS = [1700, 1050, 1350, 1150, 1150, 1400];
const LAST = 6;

function fmt(n: number): string {
  return `MVR ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function WapProScreen(): JSX.Element {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setStep(LAST);
      return;
    }
    const ms = step >= LAST ? 2800 : STEP_MS[step] ?? 1300;
    const t = setTimeout(() => setStep((s) => (s >= LAST ? 0 : s + 1)), ms);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <div dir="rtl" className="app-screen h-full w-full overflow-hidden bg-background text-foreground" style={PALETTE}>
      {step <= 1 ? <BuildStage step={step} /> : <CompareStage step={step} />}
    </div>
  );
}

/* ── Stage 1 — build the item request ─────────────────────────────────── */
function BuildStage({ step }: { step: number }): JSX.Element {
  const sending = step >= 1;
  return (
    <div key="build" className="ws-fade flex h-full flex-col">
      <div className="border-b border-border bg-card px-5 py-3">
        <div className="flex items-center gap-2.5">
          <span className="rounded-full px-2 py-0.5 text-[0.6rem] font-semibold" style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}>
            ޑްރާފްޓް
          </span>
          <span className="text-[0.66rem] text-muted-foreground" style={{ fontFamily: MONO }} dir="ltr">
            RFQ-2026-041
          </span>
        </div>
        <p className="mt-1.5 text-[1.05rem] font-bold leading-tight" style={{ color: 'hsl(var(--accent-ink))', fontFamily: HEADING }}>
          އޮފީސް ފަރުނީޗަރު ހޯދުން
        </p>
      </div>

      <div className="min-h-0 flex-1 px-5 pt-3.5">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[0.64rem] font-semibold tracking-[0.04em] text-muted-foreground">
            ތަކެތި (<span style={{ fontFamily: MONO }}>3</span>)
          </span>
          <span className="inline-flex items-center gap-1 text-[0.62rem]" style={{ color: 'hsl(var(--primary))' }}>
            <Plus size={11} /> ތަކެތި އިތުރުކުރޭ
          </span>
        </div>
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full border-collapse text-[0.8rem]">
            <thead>
              <tr className="border-b border-border" style={{ background: 'hsl(var(--muted) / 0.4)' }}>
                <th className="px-3 py-2 text-start font-medium text-muted-foreground">ތަކެތި</th>
                <th className="px-3 py-2 text-start font-medium text-muted-foreground">އަދަދު</th>
                <th className="px-3 py-2 text-start font-medium text-muted-foreground">ހަމަ</th>
              </tr>
            </thead>
            <tbody>
              {ITEMS.map((it) => (
                <tr key={it.name} className="border-b border-border/70 last:border-0">
                  <td className="px-3 py-2 font-medium" style={{ color: 'hsl(var(--accent-ink))' }}>{it.name}</td>
                  <td className="px-3 py-2" style={{ fontFamily: MONO }} dir="ltr">{it.qty}</td>
                  <td className="px-3 py-2 text-muted-foreground">{it.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border-t border-border bg-card px-5 py-3">
        <span className="flex items-center justify-center gap-1.5 rounded-lg py-2 text-[0.78rem] font-semibold" style={{ background: 'hsl(var(--primary))', color: '#fff', opacity: sending ? 0.85 : 1 }}>
          {sending ? 'ފޮނުވަނީ…' : (
            <>
              ކޯޓޭޝަންއަށް ދައުވަތު ފޮނުވާ <ArrowLeft size={14} />
            </>
          )}
        </span>
      </div>
    </div>
  );
}

/* ── Stage 2 — comparison + award ─────────────────────────────────────── */
function CompareStage({ step }: { step: number }): JSX.Element {
  const submitted = Math.min(3, Math.max(0, step - 2)); // vendors that have quoted
  const allIn = submitted >= 3;
  const awarded = step >= LAST;

  return (
    <div key="compare" className="ws-fade flex h-full flex-col">
      {/* header */}
      <div className="flex items-start justify-between gap-3 border-b border-border bg-card px-5 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            {awarded ? (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6rem] font-semibold" style={{ background: 'hsl(var(--success) / 0.14)', color: 'hsl(var(--success))' }}>
                <Check size={10} strokeWidth={3} /> ބަންދު
              </span>
            ) : (
              <span className="rounded-full px-2 py-0.5 text-[0.6rem] font-semibold" style={{ background: 'hsl(var(--primary) / 0.12)', color: 'hsl(var(--primary))' }}>
                ފޮނުވާފައި
              </span>
            )}
            <span className="text-[0.66rem] text-muted-foreground" style={{ fontFamily: MONO }} dir="ltr">
              RFQ-2026-041
            </span>
          </div>
          <p className="mt-1 truncate text-[1.02rem] font-bold leading-tight" style={{ color: 'hsl(var(--accent-ink))', fontFamily: HEADING }}>
            އޮފީސް ފަރުނީޗަރު ހޯދުން
          </p>
        </div>
        {awarded && (
          <div key="pdfs" className="ws-pop flex shrink-0 items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[0.68rem] font-medium">
              <FileText size={12} /> ކޯޓޭޝަން
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[0.68rem] font-semibold" style={{ background: 'hsl(var(--primary))', color: '#fff' }}>
              <Receipt size={12} /> އިންވޮއިސް
            </span>
          </div>
        )}
      </div>

      {/* comparison matrix */}
      <div className="min-h-0 flex-1 overflow-hidden px-5 pt-3">
        <div className="mb-2 text-[0.62rem] font-semibold tracking-[0.05em] text-muted-foreground">
          ކޯޓޭޝަން ތަފާތު (<span style={{ fontFamily: MONO }}>{submitted}</span> ޖަވާބު)
        </div>
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full border-collapse text-[0.78rem]">
            <thead>
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-start font-medium text-muted-foreground">ތަކެތި</th>
                {VENDORS.map((v, vi) => {
                  const has = vi < submitted;
                  return (
                    <th key={v.name} className="px-3 py-2 text-end align-bottom">
                      <div className="flex flex-col items-end gap-1">
                        <span className="font-semibold" style={{ color: has ? 'hsl(var(--accent-ink))' : 'hsl(var(--muted-foreground))' }}>
                          {v.name}
                        </span>
                        {awarded && v.awarded ? (
                          <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[0.54rem] font-semibold" style={{ background: 'hsl(var(--success) / 0.14)', color: 'hsl(var(--success))' }}>
                            <Check size={8} strokeWidth={3} /> ދިން
                          </span>
                        ) : !has ? (
                          <span className="rounded-full px-1.5 py-0.5 text-[0.54rem] font-semibold" style={{ background: 'hsl(var(--accent-cream))', color: 'hsl(var(--accent-ink))' }}>
                            ނިނމަން ހުރި
                          </span>
                        ) : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {ITEMS.map((item, ri) => {
                const lowest = Math.min(...VENDORS.map((v) => v.lines[ri] ?? 0));
                return (
                  <tr key={item.name} className="border-b border-border/70">
                    <td className="px-3 py-2 align-top">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-muted-foreground"> · <span style={{ fontFamily: MONO }} dir="ltr">{item.qty}</span> {item.unit}</span>
                    </td>
                    {VENDORS.map((v, vi) => {
                      const has = vi < submitted;
                      const price = v.lines[ri] ?? 0;
                      const isLowest = allIn && price === lowest;
                      return (
                        <td key={v.name} className="px-3 py-2 text-end" style={isLowest ? { background: 'hsl(var(--success) / 0.08)' } : undefined}>
                          {has ? (
                            <span key={`p-${submitted}`} dir="ltr" className="ws-pop inline-block" style={{ fontFamily: MONO, fontWeight: isLowest ? 600 : 400, color: isLowest ? 'hsl(var(--success))' : 'hsl(var(--foreground))' }}>
                              {fmt(price)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr className="border-t-2 font-semibold" style={{ borderColor: 'hsl(var(--border-strong))', background: 'hsl(var(--muted) / 0.45)' }}>
                <td className="px-3 py-2.5">ޖުމްލަ</td>
                {VENDORS.map((v, vi) => {
                  const has = vi < submitted;
                  const total = v.lines.reduce((a, b) => a + b, 0);
                  return (
                    <td key={v.name} className="px-3 py-2.5 text-end" style={awarded && v.awarded ? { background: 'hsl(var(--success) / 0.1)' } : undefined}>
                      {has ? (
                        <span dir="ltr" className="inline-block" style={{ fontFamily: MONO, color: awarded && v.awarded ? 'hsl(var(--success))' : 'hsl(var(--accent-ink))' }}>
                          {fmt(total)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* award summary */}
      <div className="flex min-h-[3.25rem] items-center justify-between border-t border-border bg-card px-5 py-3">
        {awarded ? (
          <>
            <div key="award" className="ws-fade flex items-center gap-2">
              <span className="inline-flex size-6 items-center justify-center rounded-full" style={{ background: 'hsl(var(--primary))', color: '#fff' }}>
                <Check size={13} strokeWidth={3} />
              </span>
              <span className="text-[0.78rem]">
                ކޯންޓްރެކްޓް ދިނީ <span className="font-semibold" style={{ color: 'hsl(var(--accent-ink))' }}>މޯލްޑިވް ފަރުނީޗަރ</span>
              </span>
            </div>
            <div className="flex items-center gap-2 text-[0.7rem]">
              <span className="text-muted-foreground">އެންމެ ބޮޑު އަގާ އަޅާ ސަލާމަތްވި</span>
              <span dir="ltr" className="rounded-md px-2 py-0.5 font-semibold" style={{ fontFamily: MONO, background: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))' }}>
                −MVR 7,150
              </span>
            </div>
          </>
        ) : (
          <span className="text-[0.72rem] text-muted-foreground">
            {allIn ? 'އެންމެ ހެޔޮ އަގު ހޮވައި ކޯންޓްރެކްޓް ދޭ' : 'ވިއްކާ ފަރާތްތަކުގެ ޖަވާބަށް އިންތިޒާރު ކުރަނީ…'}
          </span>
        )}
      </div>
    </div>
  );
}
